import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, MailCheck } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", department: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const field = "mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-[#B01E24]/60";
  const label = "text-xs font-semibold uppercase tracking-wider text-slate-500";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F7F9] p-8">
      <div className="w-full max-w-sm fade-up">
        <div className="flex items-center gap-3 mb-10">
          <img src="/dee-logo-red.png" alt="Dee Piping" className="h-10 w-auto object-contain" />
          <div>
            <div className="font-extrabold text-lg text-slate-900 tracking-tight" style={{ fontFamily: "Outfit" }}>DEE PIPING</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">SAP Agent Insights</div>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-1">Create account</h1>
        <p className="text-sm text-slate-500 mb-7">Register to access Dee Piping SAP insights</p>
        {submitted ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="register-success-panel">
            <MailCheck size={28} className="mx-auto text-emerald-600 mb-3" />
            <div className="text-sm font-semibold text-emerald-800 mb-1">Registration submitted</div>
            <p className="text-sm text-emerald-700">
              Your account is awaiting admin approval. Once approved, you'll be able to sign in and see the sections granted to you.
            </p>
            <Link to="/login" data-testid="register-success-login-link" className="inline-block mt-4 text-sm font-medium text-[#B01E24] hover:text-[#9A171D]">
              Back to sign in
            </Link>
          </div>
        ) : (
        <>
        {error && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="register-error">{error}</div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={label}>Full name</label>
            <input required value={form.name} onChange={set("name")} data-testid="register-name-input" className={field} placeholder="Your name" />
          </div>
          <div>
            <label className={label}>Email</label>
            <input type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" className={field} placeholder="you@deepiping.com" />
          </div>
          <div>
            <label className={label}>Department (optional)</label>
            <input value={form.department} onChange={set("department")} data-testid="register-department-input" className={field} placeholder="Finance / SCM / Stores" />
          </div>
          <div>
            <label className={label}>Password</label>
            <input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password-input" className={field} placeholder="Min 6 characters" />
          </div>
          <button
            type="submit" disabled={loading}
            data-testid="register-submit-button"
            className="w-full flex items-center justify-center gap-2 bg-[#B01E24] hover:bg-[#9A171D] disabled:opacity-60 text-white font-medium rounded-lg py-2.5 text-sm transition-colors"
          >
            {loading && <Loader2 size={15} className="animate-spin" />} Create Account
          </button>
        </form>
        <p className="mt-6 text-sm text-slate-500 text-center">
          Already have an account?{" "}
          <Link to="/login" data-testid="back-to-login-link" className="text-[#B01E24] hover:text-[#9A171D] font-medium">Sign in</Link>
        </p>
        </>
        )}
      </div>
    </div>
  );
}
